import {AsyncPipe, NgClass} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {FlexModule} from '@angular/flex-layout';
import {lastValueFrom} from 'rxjs';
import {ConsoleUserService} from '@core-services/console-user.service';
import {
  CreateARetailerService
} from '@features/leads/retailers/create-a-retailer/create-a-lease-dialog/create-a-retailer.service';
import {RegisterRetailer, Retailer, TenantType} from '@features/leads/retailers/retailers.model';
import {IndustrySelectComponent} from '@shared-components/industry-select/industry-select.component';
import {RegisterUserRequest} from '@ui-kit/framework/security/model/user.model';
import {LoadingService} from '@ui-kit/framework/util/service/loading.service';
import {CustomValidators} from '@ui-kit/framework/util/validators/custom-validators';
import {
  FormFieldErrorsComponent
} from '@ui-kit/webkit/components/reactive-forms/form-field-errors/form-field-errors.component';
import {SnackbarService} from "@ui-kit/framework/util/service/snackbar.service";
import {AppSharedModule} from "@modules/app-shared.module";
import {PanelType} from "@shared-components/info-panel/info-panel.component";
import {
  FormResetActionComponent
} from "@ui-kit/webkit/components/reactive-forms/form-reset-action/form-reset-action.component";

export const ERROR_MAP: Record<string, Record<string, string>> = {
  email:          { email: 'Not a valid email address', alreadyAssigned: 'User already assigned to a retailer' },
  firstName:      {},
  lastName:       {},
  companyName:    {},
  industryTypes:  {},
  websiteUrl:     { websiteUrlInvalid: 'Please enter a valid URL' },
  socialMediaUrl: { websiteUrlInvalid: 'Please enter a valid URL' },
};

@Component({
  selector: 'app-retailer-create-form',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatButtonToggleModule,
    FlexModule,
    IndustrySelectComponent,
    FormFieldErrorsComponent,
    AppSharedModule,
    NgClass,
    FormResetActionComponent,
  ],
  templateUrl: './retailer-create-form.component.html',
  styleUrls: ['./retailer-create-form.component.scss'],
})
export class RetailerCreateFormComponent implements OnInit {
  readonly offeringUuid = input.required<string>();

  readonly retailerCreated = output<Retailer>();
  readonly cancelled = output<void>();

  protected readonly loader = inject(LoadingService);
  private readonly userService = inject(ConsoleUserService);
  private readonly createRetailerService = inject(CreateARetailerService);
  private readonly destroyRef = inject(DestroyRef);
  private _snackbarService = inject(SnackbarService);

  protected readonly TenantType = TenantType;
  protected readonly selectedType = signal<TenantType>(TenantType.TENANT);

  // Email state signals
  protected readonly emailLocked   = signal(false);
  protected readonly emailEditMode = signal(false);
  protected readonly checkingEmail = signal(false);
  private emailValueBeforeEdit = '';

  protected readonly userForm = new FormGroup({
    email:     new FormControl<string | null>(null, [Validators.required, Validators.email]),
    firstName: new FormControl<string | null>(null, Validators.required),
    lastName:  new FormControl<string | null>(null, Validators.required),
  });

  protected readonly retailerForm = new FormGroup({
    companyName:    new FormControl<string | null>(null, Validators.required),
    industryTypes:  new FormControl<string[]>([], Validators.required),
    websiteUrl:     new FormControl<string | null>(null, CustomValidators.websiteUrl.bind(this)),
    socialMediaUrl: new FormControl<string | null>(null, CustomValidators.websiteUrl.bind(this)),
  });

  // Reactive email validity — drives the "Check email" button disabled state
  private readonly emailValue = toSignal(
    this.userForm.get('email')!.valueChanges,
    { initialValue: null },
  );
  protected readonly isEmailValid = computed(() => this.isValidEmail(this.emailValue() ?? ''));

  protected userCreated = false;
  private userId: number | null = null;

  protected readonly ERROR_MAP = ERROR_MAP;

  loading = toSignal(this.loader.loading$);

  ngOnInit(): void {
    this.disableUserForm();
    this.disableRetailerForm();
    this.setupEditModeReset();
  }

  protected get canSubmit(): boolean {
    return this.selectedType() === TenantType.OFFERING
      ? this.retailerForm.valid
      : this.userForm.valid && this.retailerForm.valid;
  }

  protected submitForm(): void {
    if (this.selectedType() === TenantType.OFFERING) {
      this.registerOfferingRetailer();
      return;
    }
    if (this.userCreated) {
      this.registerRetailer();
    } else {
      this.registerUser().then(ok => { if (ok) this.registerRetailer(); });
    }
  }

  protected onTypeChange(type: TenantType): void {
    this.selectedType.set(type);
    this.userCreated = false;
    this.userId = null;
    this.resetEmailState();

    if (type === TenantType.OFFERING) {
      this.resetUserForm();
      this.enableRetailerForm();
    } else {
      this.resetUserForm();
      this.resetRetailerForm();
      this.disableUserForm();
      this.disableRetailerForm();
    }
  }

  protected onCheckEmail(): void {
    // if(this.loader.loading$ || this.checkingEmail()) return;

    const email = this.userForm.get('email')!.value;
    if (!email || !this.isValidEmail(email)) return;

    this.checkingEmail.set(true);
    this.userService.getUserRetailerRelationship({email, firstname: '', lastname: ''})
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: result => {
          this.checkingEmail.set(false);
          this.handleRelationshipResult(result);
        },
        error: (err) => {
          this.checkingEmail.set(false);
          console.error(err);
          this._snackbarService.openUnexpectedErrorOccurred();
        },
      });
  }

  protected onChangeEmail(): void {
    this.emailValueBeforeEdit = this.userForm.get('email')!.value ?? '';
    this.emailLocked.set(false);
    this.emailEditMode.set(true);
  }

  protected resetForm(): void {
    this.userCreated = false;
    this.userId = null;
    this.selectedType.set(TenantType.TENANT);
    this.resetEmailState();
    this.resetUserForm();
    this.resetRetailerForm();
    this.disableUserForm();
    this.disableRetailerForm();
  }

  protected cancel(): void {
    this.cancelled.emit();
  }

  protected onCheckEmailInvalidInput(): void{
    this._snackbarService.openSnackBar('Please enter a valid email address');
  }

  protected onCantProcessRequestAtThisTime(): void {
    this._snackbarService.openCantProcessRequestAtThisTime();
  }

  private setupEditModeReset(): void {
    this.userForm.get('email')!.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(value => {
      const editModeChanged = this.emailEditMode() && value !== this.emailValueBeforeEdit;
      const alreadyAssignedEdit = this.userForm.get('email')!.hasError('alreadyAssigned');

      if (editModeChanged || alreadyAssignedEdit) {
        this.emailEditMode.set(false);
        this.emailLocked.set(false);
        this.userCreated = false;
        this.userId = null;
        this.userForm.get('firstName')!.reset();
        this.userForm.get('lastName')!.reset();
        this.disableUserForm();
        this.resetRetailerForm();
        this.disableRetailerForm();
      }
    });
  }

  private handleRelationshipResult(value: any): void {
    if (!value) {
      // No user found — fresh entry, let user fill everything
      this.emailLocked.set(true);
      this.userCreated = false;
      this.userId = null;
      this.enableUserForm();
      this.enableRetailerForm();
      return;
    }
    if (value.userId && value.retailerId) {
      // User already assigned to a retailer — block submission
      this.userCreated = false;
      this.userId = value.userId;
      this.userForm.get('firstName')!.setValue(value.firstName);
      this.userForm.get('lastName')!.setValue(value.lastName);
      this.retailerForm.get('companyName')!.setValue(value.companyName);
      this.disableUserForm();
      this.disableRetailerForm();
      this.userForm.get('email')!.setErrors({ alreadyAssigned: true });
      this.emailLocked.set(true);
      return;
    }
    if (value.userId) {
      // User exists but not yet assigned — pre-fill name and open retailer form
      this.emailLocked.set(true);
      this.userCreated = true;
      this.userId = value.userId;
      this.userForm.get('firstName')!.setValue(value.firstName);
      this.userForm.get('lastName')!.setValue(value.lastName);
      this.disableUserForm();
      this.enableRetailerForm();
      return;
    }
    // User record has no userId — treat as not found
    this.emailLocked.set(true);
    this.userCreated = false;
    this.userId = null;
    this.enableUserForm();
    this.enableRetailerForm();
  }

  private async registerUser(): Promise<boolean> {
    const newUser = new RegisterUserRequest({
      firstname: this.userForm.get('firstName')?.value,
      lastname: this.userForm.get('lastName')?.value,
      email: this.userForm.get('email')?.value,
    });
    try {
      const res = await lastValueFrom(this.userService.registerNewPortalUser(newUser));
      this.userCreated = true;
      this.userId = res.id!;
      return true;
    } catch (err: any) {
      this.userCreated = false;
      if (err.status === 400 && 'ValidationException' === err.headers.get('error_code')) {
        this._snackbarService.openSnackBar(err.headers.get('error_message'))
      } else {
        this._snackbarService.openUnexpectedErrorOccurred();
      }
      console.error(err);
      return false;
    }
  }

  private registerRetailer(): void {
    const newRetailer: RegisterRetailer = {
      companyName: this.retailerForm.get('companyName')?.value!,
      userId: this.userId,
      industries: this.retailerForm.get('industryTypes')?.value ?? [],
      websiteUrl: this.retailerForm.get('websiteUrl')?.value ?? undefined,
      socialMediaUrl: this.retailerForm.get('socialMediaUrl')?.value ?? undefined,
    };
    lastValueFrom(this.createRetailerService.registerNewRetailer(newRetailer))
      .then(res => this.retailerCreated.emit(res))
      .catch(err => {
        this._snackbarService.openSnackBar('Failed to create retailer, please try again');
        console.error('Error creating retailer:', err);
      });
  }

  private registerOfferingRetailer(): void {
    const newRetailer: RegisterRetailer = {
      companyName: this.retailerForm.get('companyName')?.value!,
      userId: null,
      industries: this.retailerForm.get('industryTypes')?.value ?? [],
      websiteUrl: this.retailerForm.get('websiteUrl')?.value ?? undefined,
      socialMediaUrl: this.retailerForm.get('socialMediaUrl')?.value ?? undefined,
    };
    lastValueFrom(this.createRetailerService.registerNewOfferingRetailer(newRetailer))
      .then(res => this.retailerCreated.emit(res))
      .catch(err => {
        this._snackbarService.openSnackBar('Failed to create offering retailer, please try again');
        console.error('Error creating offering retailer:', err);
      });
  }

  private isValidEmail(email: string): boolean {
    return !!email && !Validators.email({ value: email } as any);
  }

  private resetEmailState(): void {
    this.emailLocked.set(false);
    this.emailEditMode.set(false);
    this.emailValueBeforeEdit = '';
  }

  private resetUserForm(): void {
    this.userForm.reset();
    this.userForm.get('email')?.setErrors(null);
  }

  private enableUserForm(): void {
    this.userForm.enable();
  }

  private disableUserForm(): void {
    this.userForm.get('firstName')!.disable();
    this.userForm.get('lastName')!.disable();
  }

  private resetRetailerForm(): void {
    this.retailerForm.reset();
  }

  private enableRetailerForm(): void {
    this.retailerForm.enable();
  }

  private disableRetailerForm(): void {
    this.retailerForm.disable();
  }

  protected readonly PanelType = PanelType;
}
